import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./styles/pages/SetupPage.css";
import { apiService } from "./services/api.service";

export interface User {
  id: string;
  displayName: string;
  email: string;
  mail?: string;
  userPrincipalName?: string;
}

interface PeoplePickerProps {
  selectedUser: string;
  onUserSelect: (user: User) => void;
  placeholder?: string;
  required?: boolean;
  searchType?: 'users' | 'groups' | 'both';
}

export default function PeoplePicker({
   selectedUser,
   onUserSelect,
   placeholder = "Rechercher une personne...",
   required = false,
   searchType = 'users',
 }: PeoplePickerProps) {
   const { t } = useTranslation('setupPage');
   const [searchTerm, setSearchTerm] = useState(selectedUser);
   const [suggestions, setSuggestions] = useState<User[]>([]);
   const [isOpen, setIsOpen] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [userJustSelected, setUserJustSelected] = useState(false);
   const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
   const wrapperRef = useRef<HTMLDivElement>(null);
   const inputRef = useRef<HTMLInputElement>(null);

   // Sync searchTerm when selectedUser prop changes externally (e.g. form reset)
   useEffect(() => {
     setSearchTerm(selectedUser);
   }, [selectedUser]);

  // Calculate dropdown position relative to viewport (for position: fixed)
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap below input
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update dropdown position when it opens, suggestions change, or window resizes/scrolls
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isOpen, suggestions, updateDropdownPosition]);

  // Search for users when search term changes
  useEffect(() => {
    const searchUsers = async () => {
      // Skip search if a user was just selected
      if (userJustSelected) {
        setUserJustSelected(false);
        return;
      }

      if (searchTerm.length < 2) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = searchType === 'groups'
          ? await apiService.searchSecurityGroups(searchTerm)
          : await apiService.searchUsers(searchTerm);

        if (response.success && response.data && Array.isArray(response.data)) {
          const results: User[] = (response.data as any[]).map((item: any) => ({
            id: item.id,
            displayName: item.displayName,
            email: item.mail || item.userPrincipalName,
            mail: item.mail,
            userPrincipalName: item.userPrincipalName,
          }));

          setSuggestions(results);
          setIsOpen(true);
        } else {
          setSuggestions([]);
          setIsOpen(true);
        }
      } catch (error) {
        console.error(`[PeoplePicker] Error searching ${searchType}:`, error);
        setSuggestions([]);
        setIsOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, searchType]);

  const handleUserSelect = (user: User) => {
    setUserJustSelected(true);
    setSearchTerm(user.displayName);
    onUserSelect(user);
    setSuggestions([]);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserJustSelected(false);
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const dropdownStyle: React.CSSProperties = dropdownPosition ? {
    position: 'fixed',
    top: `${dropdownPosition.top}px`,
    left: `${dropdownPosition.left}px`,
    width: `${dropdownPosition.width}px`,
  } : {};

  return (
    <div className="people-picker" ref={wrapperRef}>
      <div className="fluent-input-container">
        <span className="fluent-input-icon">👤</span>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="fluent-input"
          required={required}
          autoComplete="off"
        />
        {isLoading && <span className="people-picker-loading">⏳</span>}
      </div>

      {isOpen && suggestions.length > 0 && dropdownPosition && (
        <div className="people-picker-dropdown" style={dropdownStyle}>
          <div className="people-picker-list">
            {suggestions.map((user) => (
              <div
                key={user.id}
                className="people-picker-item"
                onClick={() => handleUserSelect(user)}
                role="option"
                aria-selected="false"
              >
                <div className="people-picker-avatar">
                  {user.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="people-picker-details">
                  <div className="people-picker-name">{user.displayName}</div>
                  <div className="people-picker-email">{user.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
       {isOpen && suggestions.length === 0 && searchTerm.length >= 2 && !isLoading && dropdownPosition && (
         <div className="people-picker-dropdown" style={dropdownStyle}>
           <div className="people-picker-list">
             <div className="people-picker-no-results">
               {t('peoplePicker.noResults', { searchTerm })}
             </div>
           </div>
         </div>
       )}
    </div>
  );
}
